import {useEffect, useState} from "react";
import {useMapContext} from "base/map";
import Typography from "@mui/material/Typography";
import {usePoisIsochroneContext} from "poi/map";
import {theme} from "Theme";

import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Box from "@mui/material/Box";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

const HelpTooltip = ({children, title, onClose}) => {
    // Estilos del triángulo según la posición
    const getTriangleStyles = () => {
        const triangleSize = "8px";

        const baseStyles = {
            content: '""',
            position: "absolute",
            width: 0,
            height: 0,
            borderStyle: "solid",
        };

        return {
            ...baseStyles,
            left: `-${triangleSize}`,
            top: 20,
            borderWidth: `${triangleSize} ${triangleSize} ${triangleSize} 0`,
            borderColor: `transparent ${theme.palette.info.main} transparent transparent`,
        };
    };

    const triangleStyles = getTriangleStyles();

    return (
        <Box
            sx={{
                position: "relative",
                display: "inline-block",
                "&::before": triangleStyles,
            }}
        >
            <Alert
                severity="info"
                variant="filled"
                icon={<InfoOutlinedIcon />}
                onClose={onClose}
            >
                {title && (
                    <AlertTitle sx={{marginBottom: title && children ? 1 : 0}}>
                        {title}
                    </AlertTitle>
                )}
                {children && <Typography variant="body2">{children}</Typography>}
            </Alert>
        </Box>
    );
};

const IsocronasFilterHelper = () => {
    const {selectedPoint, showToc} = useMapContext();
    const {selectedTransport, selectedTravelTime, selectedCategories} =
        usePoisIsochroneContext();

    // Estado para forzar re-render cuando se cierra un Alert
    const [forceUpdate, setForceUpdate] = useState(0);

    const isHelpClosed = helpType => {
        return localStorage.getItem(`help_closed_${helpType}`) === "true";
    };

    const setHelpClosed = helpType => {
        localStorage.setItem(`help_closed_${helpType}`, "true");
        // Forzar re-render incrementando el estado
        setForceUpdate(prev => prev + 1);
    };

    const getHelper = () => {
        if (!selectedPoint) {
            if (isHelpClosed("point")) return null;
            return {
                top: 50,
                title: "Seleccione un punto de origen",
                text: (
                    <>
                        <Typography sx={{mb: 1}}>
                            ¡Hola! Bienvenido al sistema de localización de servicios de
                            la diputación de Almería.
                        </Typography>
                        <Typography>
                            Para comenzar debe seleccionar un <b>punto de origen</b> del
                            cálculo. Utilice el buscador del panel lateral para
                            encontrar una localización o haga doble click en un punto
                            del mapa.
                        </Typography>
                    </>
                ),
                onClose: () => setHelpClosed("point"),
            };
        }
        if (!selectedTransport || !selectedTravelTime) {
            if (isHelpClosed("trip")) return null;
            return {
                top: 90,
                title: "Seleccione medio de transporte y distancia",
                text: (
                    <Typography>
                        Elija en los dos componentes restantes del panel el{" "}
                        <b>medio de transporte</b> (caminando, bicicleta o automóvil) y
                        el <b>tiempo</b> (15 minutos, 30 o 60) para que el sistema pueda
                        encontrar los servicios dentro de esa distancia.
                    </Typography>
                ),
                onClose: () => setHelpClosed("trip"),
            };
        }
        if (!selectedCategories || selectedCategories.length === 0) {
            if (isHelpClosed("categories")) return null;
            return {
                top: 150,
                title: "Seleccione categorías",
                text: (
                    <Typography>
                        Ahora puede filtrar las categorías de los servicios localizados
                        y en el listado inferior se mostrarán los resultados.
                    </Typography>
                ),
                onClose: () => setHelpClosed("categories"),
            };
        }
        return null;
    };

    const helper = getHelper();

    return (
        showToc &&
        helper && (
            <Box sx={{position: "absolute", left: 365, width: 450, top: helper.top}}>
                <HelpTooltip title={helper.title} onClose={helper.onClose}>
                    {helper.text}
                </HelpTooltip>
            </Box>
        )
    );
};

export default IsocronasFilterHelper;
